import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { normalizePhoneDigits } from "@/lib/format-phone";
import type { SharedContactInfo } from "./SharedContactBubble";

interface Funnel {
  id: string;
  name: string;
  is_default: boolean | null;
}

interface Stage {
  id: string;
  name: string;
  slug: string;
  funnel_id: string | null;
  order_index: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  integrationId: string | null;
  contact: SharedContactInfo;
  mode: "save" | "chat";
}

/** Gera variantes 12 e 13 dígitos p/ Brasil (55 + DDD + N). */
function phoneVariants(digits: string): string[] {
  if (!digits) return [];
  const s = new Set<string>([digits]);
  // Se veio sem 55, tenta com
  if (!digits.startsWith("55") && (digits.length === 10 || digits.length === 11)) {
    s.add("55" + digits);
  }
  const withDdi = Array.from(s).find((v) => v.startsWith("55") && (v.length === 12 || v.length === 13));
  if (withDdi) {
    if (withDdi.length === 13) {
      // remove o 9 após DDD
      s.add(withDdi.slice(0, 4) + withDdi.slice(5));
    } else if (withDdi.length === 12) {
      s.add(withDdi.slice(0, 4) + "9" + withDdi.slice(4));
    }
  }
  return Array.from(s);
}

export function SharedContactSaveDialog({
  open,
  onOpenChange,
  workspaceId,
  integrationId,
  contact,
  mode,
}: Props) {
  const { toast } = useToast();
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phones[0] ?? "");
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [funnelId, setFunnelId] = useState<string | null>(null);
  const [stageSlug, setStageSlug] = useState<string | null>(null);
  const [existingLead, setExistingLead] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const digits = useMemo(() => normalizePhoneDigits(phone), [phone]);

  useEffect(() => {
    if (!open) return;
    setName(contact.name);
    setPhone(contact.phones[0] ?? "");
  }, [open, contact]);

  // Load funnels
  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("sales_funnels")
        .select("id, name, is_default")
        .eq("workspace_id", workspaceId)
        .order("is_default", { ascending: false })
        .order("order_index", { ascending: true });
      if (cancelled) return;
      if (error) {
        console.error("funnels error", error);
        toast({ title: "Erro ao carregar funis", variant: "destructive" });
      } else {
        const rows = (data as Funnel[]) ?? [];
        setFunnels(rows);
        const def = rows.find((f) => f.is_default) ?? rows[0];
        if (def) setFunnelId(def.id);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, toast]);

  // Load stages for selected funnel
  useEffect(() => {
    if (!funnelId) {
      setStages([]);
      setStageSlug(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("lead_stages")
        .select("id, name, slug, funnel_id, order_index")
        .eq("workspace_id", workspaceId)
        .eq("funnel_id", funnelId)
        .order("order_index", { ascending: true });
      if (cancelled) return;
      if (error) {
        console.error("stages error", error);
        return;
      }
      const rows = (data as Stage[]) ?? [];
      setStages(rows);
      setStageSlug(rows[0]?.slug ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [funnelId, workspaceId]);

  // Check for existing lead by phone
  useEffect(() => {
    if (!open || !workspaceId || !digits) {
      setExistingLead(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const variants = phoneVariants(digits);
      if (variants.length === 0) return;
      const { data } = await supabase
        .from("leads")
        .select("id, name")
        .eq("workspace_id", workspaceId)
        .in("phone", variants)
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setExistingLead((data as any) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, digits]);

  const canSubmit = !!name.trim() && !!digits && !!funnelId && !!stageSlug && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !funnelId || !stageSlug) return;
    setSubmitting(true);
    try {
      let leadId = existingLead?.id ?? null;

      if (!leadId) {
        // Create the lead scoped to workspace/funnel/stage
        const canonicalPhone = digits.startsWith("55") ? digits : `55${digits}`;
        const { data: created, error } = await supabase
          .from("leads")
          .insert({
            workspace_id: workspaceId,
            name: name.trim(),
            phone: canonicalPhone,
            funnel_id: funnelId,
            stage: stageSlug,
          })
          .select("id")
          .single();
        if (error) throw error;
        leadId = created!.id;

        await supabase
          .from("lead_funnel_memberships")
          .upsert(
            { lead_id: leadId, funnel_id: funnelId, stage: stageSlug },
            { onConflict: "lead_id,funnel_id" },
          );
      }

      if (mode === "chat" && leadId) {
        const { data, error } = await supabase.functions.invoke("start-conversation", {
          body: {
            workspaceId,
            leadId,
            channel: "whatsapp",
            integrationId,
          },
        });
        if (error) throw error;
        if (data?.conversation?.id) {
          window.location.href = `/conversations?conversation_id=${data.conversation.id}&channel=whatsapp`;
        }
      } else {
        toast({
          title: existingLead ? "Lead atualizado" : "Lead cadastrado",
          description: `${name.trim()} foi salvo no funil.`,
        });
      }
      onOpenChange(false);
    } catch (e) {
      console.error("save contact error", e);
      toast({
        title: "Erro ao salvar contato",
        description: e instanceof Error ? e.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "chat" ? "Iniciar conversa" : "Adicionar contato"}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Nome</Label>
              <Input id="contact-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Telefone</Label>
              <Input id="contact-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            {existingLead && (
              <p className="text-xs text-muted-foreground">
                Já existe um lead com esse telefone: <strong>{existingLead.name}</strong>.
                {mode === "chat"
                  ? " A conversa será aberta com ele."
                  : " O funil/coluna serão atualizados."}
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Funil</Label>
              <Select value={funnelId ?? undefined} onValueChange={setFunnelId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um funil" />
                </SelectTrigger>
                <SelectContent>
                  {funnels.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Coluna</Label>
              <Select value={stageSlug ?? undefined} onValueChange={setStageSlug}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma coluna" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.slug}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === "chat" ? "Iniciar conversa" : "Salvar contato"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
